import { DebugLog } from '../../../common/js/debug-log';
import {
  isInterstitialAdUnit,
  isMediumRectangleAdUnit,
  isOptinVideoAdUnit,
  AD_UNIT_TYPE
} from '../../../common/js/adUnit';
import { BROWSERS } from '../../../common/js/browser-utils';
import { preloadAsset } from '../../../common/js/network-utils';
import { unmarshal as createConfig } from '@ogury/unmarshaller';
import { initRaven } from '../../../common/js/sentry';
import CONFIG from '../../../common/js/config';
import {
  initAnalytics,
  sendCreativeLoadInit
} from '../../../common/js/TrackAnalyticsHelper';
import { FormatContext } from './strategies/loading-strategy';
import { LoadingStrategyFactory } from './strategies/loading-strategy-factory';
import { MRAIDProvider } from '../../../common/js/mraid/mraid-provider';
import { isFeatureEnable } from '../../../common/js/features-enabling';

interface PrepareFormatInput {
  formatName: FormatName;
  options: {
    context: AdContext;
    getParameter: (key: string) => any;
  };
  formatUnmarshaller: Record<string, any>;
}

interface BrowserConfig {
  preload?: boolean;
  type?: string;
}

export function prepareFormat(
  { formatName, options, formatUnmarshaller }: PrepareFormatInput,
  done: (formatContext: FormatContext) => void = () => {}
): void {
  const context = options.context;

  if (typeof context.showOptoutLink === 'undefined') {
    context.showOptoutLink = true;
  }

  if (typeof context.adUnitType === 'undefined') {
    context.adUnitType = 'interstitial';
  }

  initRaven(formatName, context);

  (window as any).OG_DEVICE_TYPE = context.deviceType || undefined;
  const desc = Object.getOwnPropertyDescriptor(window, 'MRAID_ENV');
  if (!desc || desc.writable || desc.configurable) {
    (window as any).MRAID_ENV = (window as any).MRAID_ENV || {};
  }

  const formatContext = initFormatConfig(options, formatUnmarshaller);

  initAnalytics(
    formatName,
    formatContext.config,
    formatContext.adContext,
    !isInterstitialAdUnit(formatContext.adContext.adUnitType || '') &&
      !isOptinVideoAdUnit(formatContext.adContext.adUnitType || '')
  );

  sendCreativeLoadInit();

  new MRAIDProvider().provideMraid(
    window,
    {
      isMraidCompliant: formatContext.config.isMraidCompliant,
      appExchange: formatContext.adContext._appExchange,
      dsp: formatContext.adContext.dsp
    },
    () => {
      //TODO: adContext should be a store and get this value from AdContainerAdapter
      formatContext.adContext.isMraidPolyfilled = (window as any).mraid.__ogyPolyfill;
      done(formatContext);
    }
  );
}

export function initFormatConfig(
  options: PrepareFormatInput['options'],
  formatUnmarshaller: Record<string, any>
): FormatContext {
  DebugLog.log('Get configuration', options as any);

  const formatConfig = getFormatConfig(options, formatUnmarshaller);
  DebugLog.log('Format config founded', formatConfig);

  const config = createConfig<FormatConfig>(
    customResolver.bind(null, options),
    formatConfig
  );
  DebugLog.log('Format config unmarshalled', config);

  const formatContext = LoadingStrategyFactory.getInstance().alterConfiguration(
    { config, adContext: options.context }
  );

  return formatContext;
}

export function preloadBrowser(browserConfig?: BrowserConfig): void {
  if (
    isFeatureEnable('oguryBrowser') &&
    browserConfig &&
    browserConfig.preload &&
    browserConfig.type !== BROWSERS.CHROME_TAB &&
    browserConfig.type !== BROWSERS.DEFAULT_BROWSER
  ) {
    // while not required, this helps speeding up the browser display
    preloadAsset(CONFIG.SDK3_BROWSER_URL);
  }
}

function getFormatConfig(
  options: PrepareFormatInput['options'],
  formatUnmarshaller: Record<string, any>
): any {
  let configKey = options.context.adUnitType || '';
  if (options.context.adUnitType === AD_UNIT_TYPE.IN_ARTICLE) {
    configKey += `/${options.context.inArticleType}`;
  }

  return formatUnmarshaller[configKey] || formatUnmarshaller['default'];
}

function customResolver(
  options: PrepareFormatInput['options'],
  key: string
): any {
  // force 300x250 size on medium_rectangle external delivery
  const isMediumRectangle = isMediumRectangleAdUnit(
    options.context.adUnitType || ''
  );
  const isAppExchangeOgury =
    options.context._appExchange &&
    options.context._appExchange.toLowerCase() !== 'ogury';
  const isPreventFullWidth = key === 'prevent_fullwidth';

  if (isMediumRectangle && isAppExchangeOgury && isPreventFullWidth) {
    return true;
  }

  return options.getParameter(key);
}
